import {
  getAssistantSceneActionIds,
  getAssistantSceneValueIds,
  type AssistantActionId,
  type AssistantValueId
} from "./assistantPolicy.js";
import { getAssistantSceneStateContextIds } from "./assistantSceneState.js";

export type SocraticStage = "claim" | "evidence" | "reasoning" | "revision";
export type SocraticMisconceptionTag =
  | "evidence-missing"
  | "concept-confusion"
  | "causality-overstated"
  | "scope-overreach"
  | "expression-unclear";

export type SocraticPromptLayers = Readonly<Record<SocraticStage, string>>;

type SceneGuideSeed = {
  readonly sceneId: string;
  readonly chapterId: "chapter-1" | "chapter-2" | "chapter-3" | "chapter-4" | "conclusion";
  readonly objectiveLabel: string;
  readonly sceneStep: "observe" | "operate" | "judge" | "verify" | "journal" | "assess" | "audit" | "report";
  readonly order: number;
  readonly guideStepLabel: string;
  readonly phase: "observe" | "hypothesize" | "operate" | "judge" | "verify" | "revise";
  readonly evidenceIds: readonly (
    | "P01" | "P02" | "P03" | "P04" | "P05" | "P06"
    | "P07" | "P08" | "P09" | "P10" | "P11" | "P12"
  )[];
  readonly ruleIds: readonly string[];
  readonly sceneFocus: string;
  readonly successCondition: string;
  readonly commonMisconceptionTags: readonly SocraticMisconceptionTag[];
  readonly localFallback: Readonly<{ feedback: string; evidencePrompt: string }>;
};

const sceneGuideSeeds = [
  {
    sceneId: "C1-00", chapterId: "chapter-1", objectiveLabel: "观察四季长卷", sceneStep: "observe", order: 0,
    guideStepLabel: "观察四季变化", phase: "observe", evidenceIds: ["P01"], ruleIds: ["c1.process.observe"],
    sceneFocus: "四季中的生物变化", successCondition: "区分自然变化与观察结论，并指出时间依据。",
    commonMisconceptionTags: ["concept-confusion", "evidence-missing"],
    localFallback: { feedback: "先把看到的自然变化和据此形成的判断分开，再回到 P01 找时间线索。", evidencePrompt: "P01 中哪个词能限定你这次判断的时间范围？" }
  },
  {
    sceneId: "C1-01", chapterId: "chapter-1", objectiveLabel: "三类现象辨析", sceneStep: "judge", order: 1,
    guideStepLabel: "辨析现象类型", phase: "judge", evidenceIds: ["P01", "P02", "P03"], ruleIds: ["c1.classify.biological", "c1.classify.weather", "c1.classify.incidental"],
    sceneFocus: "物候现象与季节背景的区别", successCondition: "依据生物变化、时节联系和重复性完成三分类。",
    commonMisconceptionTags: ["concept-confusion", "evidence-missing"],
    localFallback: { feedback: "先用课文给物候现象划定边界，再逐项核对生物变化、时节和重复出现这三层信息。", evidencePrompt: "你选的现象同时具备哪两条课文线索？" }
  },
  {
    sceneId: "C1-02", chapterId: "chapter-1", objectiveLabel: "重建四季时序", sceneStep: "operate", order: 2,
    guideStepLabel: "重建物候时序", phase: "operate", evidenceIds: ["P01"], ruleIds: ["c1.timeline.sequence"],
    sceneFocus: "四季物候出现的先后", successCondition: "按课文先后词重建四季物候顺序。",
    commonMisconceptionTags: ["concept-confusion", "evidence-missing"],
    localFallback: { feedback: "先按 P01 的先后词重建变化顺序，再检查有没有把背景景象当成生物变化。", evidencePrompt: "哪一个先后词能证明你调整后的相邻两项？" }
  },
  {
    sceneId: "C1-03", chapterId: "chapter-1", objectiveLabel: "核验证据并写入观察册", sceneStep: "journal", order: 3,
    guideStepLabel: "核验并修订观察记录", phase: "revise", evidenceIds: ["P01", "P02", "P03"], ruleIds: ["c1.process.verify", "c1.process.journal"],
    sceneFocus: "物候概念与自然现象", successCondition: "用课文证据核验判断并写出边界清楚的观察记录。",
    commonMisconceptionTags: ["scope-overreach", "evidence-missing"],
    localFallback: { feedback: "先写出判断，再分别补上课文现象和它与时节的联系，最后收窄超出课文的表述。", evidencePrompt: "你的哪一句还缺少 P01 至 P03 的直接支撑？" }
  },
  {
    sceneId: "C2-00", chapterId: "chapter-2", objectiveLabel: "沿时间尺记录桃树观察", sceneStep: "observe", order: 4,
    guideStepLabel: "观察活的对象", phase: "observe", evidenceIds: ["P04"], ruleIds: ["c2.process.observe"],
    sceneFocus: "时间尺上的桃树物候变化", successCondition: "走完四个时点，并记录一次桃树观察。",
    commonMisconceptionTags: ["concept-confusion"],
    localFallback: { feedback: "先沿时间尺看完四个时点，再点击桃树记录一次观察，然后区分生命信号和气象条件。", evidencePrompt: "P04 中哪处表述能连接这些观察与季节变化？" }
  },
  {
    sceneId: "C2-01", chapterId: "chapter-2", objectiveLabel: "找出活的仪器", sceneStep: "judge", order: 5,
    guideStepLabel: "判断活的仪器", phase: "judge", evidenceIds: ["P04"], ruleIds: ["c2.instrument.peach", "c2.instrument.swallow"],
    sceneFocus: "物候观测中的活的仪器", successCondition: "指出生物对象能够反映气候条件的课文依据。",
    commonMisconceptionTags: ["concept-confusion", "evidence-missing"],
    localFallback: { feedback: "先说明你选择的对象是什么，再从 P04 找它能够反映气候条件的依据。", evidencePrompt: "你的选择与 P04 对“活的仪器”的限定怎样对应？" }
  },
  {
    sceneId: "C2-03", chapterId: "chapter-2", objectiveLabel: "确认本章观察记录", sceneStep: "journal", order: 6,
    guideStepLabel: "修订本章观察记录", phase: "revise", evidenceIds: ["P04", "P05"], ruleIds: ["c2.process.read-case", "c2.process.journal"],
    sceneFocus: "物候观测对农业的参考作用", successCondition: "结合观测与案例修订农时记录，并保留因果边界。",
    commonMisconceptionTags: ["causality-overstated", "scope-overreach"],
    localFallback: { feedback: "先比较记录到的变化，再说明这条记录怎样支持农时判断，最后检查因果是否说得过满。", evidencePrompt: "P05 中哪句话能直接支撑你提出的调整？" }
  },
  {
    sceneId: "C3-00", chapterId: "chapter-3", objectiveLabel: "提出四因素问题", sceneStep: "observe", order: 7,
    guideStepLabel: "提出影响因素假设", phase: "hypothesize", evidenceIds: ["P06"], ruleIds: [],
    sceneFocus: "影响物候来临迟早的因素", successCondition: "提出可分别检验纬度、经度、高下和古今差异的问题。",
    commonMisconceptionTags: ["concept-confusion", "expression-unclear"],
    localFallback: { feedback: "先把待解释的现象写清楚，再从 P06 后面的段落寻找能够分别检验的变量。", evidencePrompt: "你的判断准备控制哪些条件，只比较哪一个变量？" }
  },
  {
    sceneId: "C3-01", chapterId: "chapter-3", objectiveLabel: "纬度实验", sceneStep: "operate", order: 8,
    guideStepLabel: "操作纬度实验", phase: "operate", evidenceIds: ["P07"], ruleIds: ["c3.latitude.mastery", "c3.latitude.operated", "c3.latitude.evidence"],
    sceneFocus: "纬度与物候差异", successCondition: "在其他条件可比时，用约 20 天或约 10 天的证据解释纬度差异。",
    commonMisconceptionTags: ["causality-overstated", "evidence-missing"],
    localFallback: { feedback: "先保持其他条件不变，再用 P07 的比较对象、时节和日数核对纬度推理。", evidencePrompt: "P07 的哪组比较能支撑你的这一步推理？" }
  },
  {
    sceneId: "C3-02", chapterId: "chapter-3", objectiveLabel: "经度与海陆实验", sceneStep: "operate", order: 9,
    guideStepLabel: "操作经度实验", phase: "operate", evidenceIds: ["P08"], ruleIds: ["c3.longitude.mastery", "c3.longitude.operated", "c3.longitude.evidence"],
    sceneFocus: "经度所体现的海陆差异", successCondition: "在纬度接近的前提下，用海陆位置解释春季物候差异。",
    commonMisconceptionTags: ["causality-overstated", "evidence-missing"],
    localFallback: { feedback: "先核对两地纬度是否接近，再用 P08 检查海陆位置与春季物候的联系。", evidencePrompt: "你的结论怎样连接 P08 中的地点比较和物候差异？" }
  },
  {
    sceneId: "C3-03", chapterId: "chapter-3", objectiveLabel: "高下与逆温实验", sceneStep: "operate", order: 10,
    guideStepLabel: "操作高下实验", phase: "operate", evidenceIds: ["P09"], ruleIds: ["c3.altitude.mastery", "c3.altitude.operated", "c3.altitude.evidence"],
    sceneFocus: "高下差异与特殊情形", successCondition: "说明一般高下规律，并把逆温作为有条件的特殊情形。",
    commonMisconceptionTags: ["scope-overreach", "concept-confusion"],
    localFallback: { feedback: "先写出通常规律，再把 P09 的特殊条件作为边界，避免用例外替代一般判断。", evidencePrompt: "你的表述在哪个条件下需要用 P09 的例外来修订？" }
  },
  {
    sceneId: "C3-04", chapterId: "chapter-3", objectiveLabel: "古今差异实验", sceneStep: "operate", order: 11,
    guideStepLabel: "操作古今实验", phase: "operate", evidenceIds: ["P10"], ruleIds: ["c3.time.mastery", "c3.time.operated", "c3.time.evidence"],
    sceneFocus: "物候的古今差异", successCondition: "比较同类十年平均记录，并正确限定平均约 9 天的结论。",
    commonMisconceptionTags: ["evidence-missing", "scope-overreach"],
    localFallback: { feedback: "先确认比较的是同类记录和相近时段，再用 P10 检查古今差异的证据链。", evidencePrompt: "P10 中哪两个时间段构成了你的比较依据？" }
  },
  {
    sceneId: "C3-05", chapterId: "chapter-3", objectiveLabel: "补齐四因素证据表", sceneStep: "journal", order: 12,
    guideStepLabel: "核验四因素证据", phase: "verify", evidenceIds: ["P06", "P07", "P08", "P09", "P10"], ruleIds: [],
    sceneFocus: "四项物候影响因素", successCondition: "为每项结论匹配证据与边界条件，且不混用变量。",
    commonMisconceptionTags: ["concept-confusion", "evidence-missing", "scope-overreach"],
    localFallback: { feedback: "把总判断拆成因素、证据和适用条件三列，再逐项检查是否混用了不同变量。", evidencePrompt: "哪一个因素的证据与推理之间还缺少连接说明？" }
  },
  {
    sceneId: "C4-01", chapterId: "chapter-4", objectiveLabel: "匹配六项生产用途", sceneStep: "operate", order: 13,
    guideStepLabel: "匹配生产用途", phase: "operate", evidenceIds: ["P11"], ruleIds: ["c4.process.read-p11", "c4.use.sowing", "c4.use.zoning", "c4.use.forestry", "c4.use.introduction", "c4.use.pest-control", "c4.use.mountain-observation", "c4.process.observe"],
    sceneFocus: "物候资料的生产用途", successCondition: "将六项用途分别匹配到服务对象、行动和课文依据。",
    commonMisconceptionTags: ["expression-unclear", "evidence-missing"],
    localFallback: { feedback: "先提出一种用途，再用 P11 核对它服务的对象和具体行动，避免只写宽泛作用。", evidencePrompt: "P11 中哪一处能直接支持你提出的用途？" }
  },
  {
    sceneId: "C4-03", chapterId: "chapter-4", objectiveLabel: "从观察写入生产记录", sceneStep: "journal", order: 14,
    guideStepLabel: "修订生产记录", phase: "revise", evidenceIds: ["P11", "P12"], ruleIds: ["c4.process.verify-p11-p12", "c4.process.journal"],
    sceneFocus: "物候资料的参考边界", successCondition: "以主张、证据和推理形成生产记录，并收紧越界表述。",
    commonMisconceptionTags: ["scope-overreach", "expression-unclear"],
    localFallback: { feedback: "先写主张，再补课文依据和推理，最后把超出 P11、P12 范围的说法收回来。", evidencePrompt: "你准备依据哪一处课文内容修订原来的表述？" }
  },
  {
    sceneId: "F01", chapterId: "conclusion", objectiveLabel: "五组知识测验", sceneStep: "assess", order: 15,
    guideStepLabel: "结课判断追问", phase: "judge", evidenceIds: ["P01", "P02", "P03", "P04", "P05", "P06", "P07", "P08", "P09", "P10", "P11", "P12"], ruleIds: [],
    sceneFocus: "本组测验后的判断", successCondition: "完成本地测验，并能为待核对判断指出对应课文证据。",
    commonMisconceptionTags: ["evidence-missing", "concept-confusion"],
    localFallback: { feedback: "先圈出当前表述的核心主张，再选择能直接支持它的课文段落，并说明两者的对应关系。", evidencePrompt: "你选的段落中哪一句能直接支撑这个主张？" }
  },
  {
    sceneId: "F02", chapterId: "conclusion", objectiveLabel: "AI 草稿审校", sceneStep: "audit", order: 16,
    guideStepLabel: "审校并修订表述", phase: "revise", evidenceIds: ["P03", "P04", "P05", "P07", "P08", "P09", "P10", "P11", "P12"], ruleIds: [],
    sceneFocus: "AI 草稿中的证据与表述边界", successCondition: "定位草稿的证据缺口或越界措辞，并由学生完成修订。",
    commonMisconceptionTags: ["scope-overreach", "expression-unclear", "evidence-missing"],
    localFallback: { feedback: "按主张、证据、推理和修订四步审校当前表述，先定位证据链最薄弱的一步。", evidencePrompt: "哪一处表述需要依据已选段落收窄或改写？" }
  },
  {
    sceneId: "F03", chapterId: "conclusion", objectiveLabel: "双维能力档案", sceneStep: "report", order: 17,
    guideStepLabel: "查看学习报告", phase: "revise", evidenceIds: [], ruleIds: [],
    sceneFocus: "本次学习记录", successCondition: "查看本地生成的能力档案，并识别仍需核验的结论。",
    commonMisconceptionTags: ["evidence-missing", "expression-unclear"],
    localFallback: { feedback: "回看已经形成的主张和证据链，再标出仍需核验的限定条件。", evidencePrompt: "哪一条结论最需要补充适用范围？" }
  }
] as const satisfies readonly SceneGuideSeed[];

export type SharedSceneId = (typeof sceneGuideSeeds)[number]["sceneId"];
export type SocraticStep = (typeof sceneGuideSeeds)[number] & {
  readonly guideStepId: string;
  readonly objectiveId: string;
  readonly currentItemIds: readonly string[];
  readonly allowedActionIds: readonly AssistantActionId[];
  readonly allowedValueIds: readonly AssistantValueId[];
  readonly availableEvidenceIds: SceneGuideSeed["evidenceIds"];
  readonly layeredPrompts: SocraticPromptLayers;
};
export type SharedSceneGuideEntry = SocraticStep;

function sceneSlug(sceneId: SharedSceneId): string {
  return sceneId.toLowerCase();
}

function currentItemIdsForScene(
  sceneId: SharedSceneId,
  slug: string,
  ruleIds: readonly string[]
): readonly string[] {
  const sceneStateIds = getAssistantSceneStateContextIds(sceneId);
  if (sceneId === "F01") {
    return [
      "item.f01.core", "item.f01.structure", "item.f01.methods", "item.f01.case-1962", "item.f01.attitude",
      ...ruleIds,
      ...sceneStateIds
    ];
  }
  if (sceneId === "F02") {
    return [
      "item.f02.classify", "item.f02.evidence", "item.f02.rewrite", "item.f02.done",
      ...ruleIds,
      ...sceneStateIds
    ];
  }
  return [`item.${slug}.primary`, `item.${slug}.evidence`, ...ruleIds, ...sceneStateIds];
}

function layeredPromptsForSeed(seed: (typeof sceneGuideSeeds)[number]): SocraticPromptLayers {
  if (seed.sceneId === "F01") {
    return {
      claim: "完成本地作答后，你想先核对哪一个判断？",
      evidence: "你选的段落中哪一句能直接支撑刚才的判断？",
      reasoning: "这条证据怎样支持你的判断，而不只是和它相关？",
      revision: "依据这条证据，你会怎样收紧刚才判断的范围？"
    };
  }
  if (seed.sceneId === "F02") {
    return {
      claim: "你想先核对草稿中的哪一个可验证主张？",
      evidence: "你所选的哪句证据最能指出原表述的边界？",
      reasoning: "这句证据为什么能支持保留或修正这条陈述？",
      revision: "只调整证据不能支持的词语后，你会怎样重述？"
    };
  }
  return {
    claim: `围绕${seed.sceneFocus}，你能先写出一个只包含核心判断且可核验的主张吗？`,
    evidence: seed.localFallback.evidencePrompt,
    reasoning: `这条证据为什么能支持关于${seed.sceneFocus}的主张，而不只是与它相关？`,
    revision: "依据这条证据，你会怎样收紧原来主张的范围？"
  };
}

/** The complete Socratic contract consumed by browser and server adapters. */
export const SOCRATIC_STEPS: readonly SocraticStep[] = sceneGuideSeeds.map((seed) => {
  const slug = sceneSlug(seed.sceneId);
  return {
    ...seed,
    guideStepId: `guide.${slug}.primary`,
    objectiveId: `objective.${slug}.mastery`,
    currentItemIds: currentItemIdsForScene(seed.sceneId, slug, seed.ruleIds),
    allowedActionIds: getAssistantSceneActionIds(seed.sceneId),
    allowedValueIds: getAssistantSceneValueIds(seed.sceneId),
    availableEvidenceIds: seed.evidenceIds,
    layeredPrompts: layeredPromptsForSeed(seed)
  };
});

export const SHARED_SCENE_GUIDE_CATALOG: readonly SharedSceneGuideEntry[] = SOCRATIC_STEPS;
